/**
 * Global "Powered by StudioMV" badge — pinned bottom-right on every page.
 *
 * Visibility rules:
 *  • On platform pages (/, /login, /admin, /cms, /pricing, …)  → always visible.
 *  • On a salon's public site (/salon/:slug/…)                 → hidden if the salon's
 *    active plan has feature flag `hide_studiomv_badge`.
 *  • On the page-builder preview iframe (/__preview__)         → always hidden.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, matchPath } from "react-router-dom";
import api from "@/lib/api";

const STYLE = {
  display: "none",
  boxSizing: "border-box",
  width: "178px",
  height: "40px",
  padding: "8px 12px",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  borderRadius: "0px",
  background: "#000",
  position: "fixed",
  bottom: "16px",
  right: "16px",
  textDecoration: "none",
  zIndex: 10000,
};

export default function StudioMVBadge() {
  const location = useLocation();
  const [salonFeatures, setSalonFeatures] = useState(null);

  // Detect if we are inside /salon/:slug/...
  const salonSlug = useMemo(() => {
    const m =
      matchPath("/salon/:slug", location.pathname) ||
      matchPath("/salon/:slug/account", location.pathname) ||
      matchPath("/salon/:slug/blog/:postSlug", location.pathname) ||
      matchPath("/salon/:slug/:pageSlug", location.pathname);
    return m?.params?.slug || null;
  }, [location.pathname]);

  useEffect(() => {
    if (!salonSlug) { setSalonFeatures(null); return undefined; }
    let cancelled = false;
    api.get(`/public/salon/${salonSlug}`)
      .then(({ data }) => { if (!cancelled) setSalonFeatures(data.plan_features || []); })
      .catch(() => { if (!cancelled) setSalonFeatures([]); });
    return () => { cancelled = true; };
  }, [salonSlug]);

  // Builder-Preview iframe → no badge
  if (location.pathname.startsWith("/__preview__")) return null;

  // Salon-pages with whitelabel feature → no badge
  if (salonSlug && Array.isArray(salonFeatures) && salonFeatures.includes("hide_studiomv_badge")) {
    return null;
  }

  return (
    <Link
      to="/"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="studiomv-badge"
      aria-label="Powered by StudioMV"
      style={STYLE}
    >
      <span
        style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          color: "#fff",
          fontWeight: 600,
          fontSize: "16px",
          letterSpacing: "0.18em",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        STUDIO<span style={{ color: "#C87A5F" }}>MV</span>
      </span>
    </Link>
  );
}
